import { TokenService } from '../components/auth/TokenService';
import { PlanUpgradeRequiredError } from '../error';
import { snakeToCamel } from '../utils/caseConversion';

export const waitForToken = async () => {
  return new Promise(resolve => {
    const interval = setInterval(() => {
      const token = TokenService.getToken();
      if (token) {
        clearInterval(interval);
        resolve(token);
      }
    }, 100);
  });
};

// Throws an Error annotated with the HTTP status code so callers can
// distinguish 404 from other failures (e.g. show a "not found" UI vs a
// generic error). Existing callers that read err.message continue to work.
const throwHttpError = (
  status: number,
  message: string,
  detail?: unknown
): never => {
  const err = new Error(message) as Error & {
    status?: number;
    detail?: unknown;
  };
  err.status = status;
  if (detail !== undefined) err.detail = detail;
  throw err;
};

// Read the response body once on error and extract the most informative
// message/detail we can. Falls back to the caller-supplied default.
// Errors are annotated with `.status` and `.detail` for callers that want
// to render structured upstream errors (e.g. FastAPI 422 validation arrays).
const throwFromResponse = async (
  response: Response,
  defaultMsg: string,
  fallbackMsg?: string
): Promise<never> => {
  let body: unknown;
  try {
    const text = await response.text();
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  } catch {
    body = undefined;
  }

  let upstreamMessage: string | undefined;
  let detail: unknown;
  if (body && typeof body === 'object') {
    const obj = body as Record<string, unknown>;
    if (typeof obj.error === 'string') upstreamMessage = obj.error;
    else if (typeof obj.detail === 'string') upstreamMessage = obj.detail;
    if ('detail' in obj) detail = obj.detail;
  } else if (typeof body === 'string' && body) {
    upstreamMessage = body;
  }

  return throwHttpError(
    response.status,
    upstreamMessage || fallbackMsg || defaultMsg,
    detail
  );
};

/**
 * Inspect a 403 response and throw a structured error when the server
 * signals a plan-upgrade requirement. No-op otherwise — the caller falls
 * through to generic error handling.
 */
const throwPlanUpgradeIfApplicable = async (
  response: Response
): Promise<void> => {
  if (response.status !== 403) return;
  // Clone before reading so the body stream is still available for
  // upstream error handling. clone is optional so this still works
  // against handcrafted test mocks that omit it.
  const json = await (response.clone?.() ?? response).json().catch(() => null);
  const { error, missingFeatures } = json || {};
  if (missingFeatures) {
    throw new PlanUpgradeRequiredError(error, missingFeatures);
  }
};

export const api = {
  get: async (
    endpoint: string,
    errorMsg?: string,
    transformResponse = true,
    needsAuth = true,
    opts?: any
  ) => {
    const authHeader = needsAuth
      ? { Authorization: `Bearer ${await waitForToken()}` }
      : ({} as { Authorization: 'string' });
    const response = await fetch(endpoint, {
      headers: {
        ...authHeader,
        'Content-Type': 'application/json',
      },
      signal: opts?.signal,
    });
    await throwPlanUpgradeIfApplicable(response);
    if (!response.ok) {
      await throwFromResponse(
        response,
        `GET request to ${endpoint} failed`,
        errorMsg
      );
    }
    const responseJson = await response.json();
    return transformResponse ? snakeToCamel(responseJson) : responseJson;
  },
  post: async (
    endpoint: string,
    body: any,
    errorMsg?: string,
    isFormData = false,
    needsAuth = true
  ) => {
    const headers = {} as any;
    if (needsAuth) {
      const token = await waitForToken();
      headers.Authorization = `Bearer ${token}`;
    }
    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }
    const response = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: isFormData ? body : JSON.stringify(body),
    });
    await throwPlanUpgradeIfApplicable(response);
    if (!response.ok) {
      await throwFromResponse(
        response,
        `POST request to ${endpoint} failed`,
        errorMsg
      );
    }
    return await response.json();
  },
  put: async (endpoint: string, body: any, errorMsg?: string) => {
    const token = await waitForToken();
    const response = await fetch(endpoint, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    await throwPlanUpgradeIfApplicable(response);
    if (!response.ok) {
      await throwFromResponse(
        response,
        `PUT request to ${endpoint} failed`,
        errorMsg
      );
    }
    return await response.json();
  },
  delete: async (endpoint: string, errorMsg?: string) => {
    const token = await waitForToken();
    const response = await fetch(endpoint, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    await throwPlanUpgradeIfApplicable(response);
    if (!response.ok) {
      await throwFromResponse(
        response,
        `DELETE request to ${endpoint} failed`,
        errorMsg
      );
    }
    return await response.json();
  },
  patch: async (endpoint: string, body: any, errorMsg?: string) => {
    const token = await waitForToken();
    const response = await fetch(endpoint, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    await throwPlanUpgradeIfApplicable(response);
    if (!response.ok) {
      await throwFromResponse(
        response,
        `PATCH request to ${endpoint} failed`,
        errorMsg
      );
    }
    return await response.json();
  },
};
